/**
 * TemplateEngine — lightweight recursive HTML template engine.
 *
 * Features:
 *   {{key}}             — variable interpolation (dot-notation supported)
 *   {{> partial}}       — include / partial (auto-loaded + cached)
 *   <!-- BEGIN: x -->   — loop block (iterates array in data)
 *   <!-- IF: x -->      — conditional block (truthy check)
 *   <!-- ELSE: x -->    — else branch
 *   <!-- ENDIF: x -->   — end conditional
 *   React islands       — component nodes anywhere in the tree via registerComponent()
 *
 * Architecture:
 *   Template string  →  Parser (tokenizer → AST)  →  Renderer (AST walker → HTML)
 *                                                       ↕
 *                                              TemplateContext (scope chain)
 *
 * React islands flow:
 *   renderTree()  →  HTML string (with <div data-react-id="…"> placeholders)
 *   container.innerHTML = html
 *   tplEngine.hydrateInto(container)  →  React components mounted into placeholders
 *
 * Caching:
 *   • templateCache — raw template strings keyed by name (avoids re-fetching)
 *   • astCache      — parsed AST keyed by template string (avoids re-parsing)
 *   • Partials registered via registerPartial() are stored in templateCache.
 */
import { parse } from "./parser"
import { renderNodes } from "./renderer"
import { TemplateContext } from "./context"
import { ComponentRegistry } from "./componentRegistry"
import { hydrateComponents, unmountComponents } from "./hydrateComponents"

// ── Placeholder ID generator ──────────────────────────────────────────────────
let _placeholderCounter = 0

/**
 * Generates a unique, DOM-safe ID for a React island placeholder element.
 *
 * @returns {string} Unique placeholder ID.
 */
function generatePlaceholderId() {
    _placeholderCounter += 1
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return `tpl-react-${_placeholderCounter}-${crypto.randomUUID()}`
    }
    return `tpl-react-${_placeholderCounter}-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export class TemplateEngine {
    /**
     * Creates a new TemplateEngine instance.
     *
     * @param {Object}   options        - Configuration.
     * @param {Function} options.loader - Async function (name) → templateString.
     *   Typically the existing loadtplTemplate from loadtplTemplate.js.
     */
    constructor(options = {}) {
        this.loader = options.loader
        this.templateCache = new Map()
        this.astCache = new Map()

        /** @type {ComponentRegistry} Registry of React island components. */
        this.componentRegistry = new ComponentRegistry()

        /**
         * Populated by renderTree() on each call.
         * Consumed by hydrateInto() to mount React islands after innerHTML injection.
         * @type {Array<{id: string, component: React.ComponentType, props: Object, childrenHtml: string}>}
         */
        this._mountQueue = []

        /**
         * React roots created by the last hydrateInto() call.
         * Stored so unmountAll() can clean them up before the next render.
         * @type {Array<import("react-dom/client").Root>}
         */
        this._reactRoots = []

        /**
         * Translation function for {{t "text"}} syntax.
         * Set via setTranslate(). Falls back to identity (returns original text).
         * @type {Function|null}
         */
        this.translate = null
    }

    // ── Template loading + caching ────────────────────────────────────────

    /**
     * Sets the translation function used by {{t "text"}} tokens.
     *
     * @param {Function} fn - A function (text: string) => string that returns the translated text.
     */
    setTranslate(fn) {
        this.translate = fn
    }

    /**
     * Fetches a template by name, returning from cache on hit.
     *
     * @param {string} name - Template slug / name.
     * @returns {Promise<string>} Raw template string.
     */
    async loadTemplate(name) {
        if (this.templateCache.has(name)) {
            return this.templateCache.get(name)
        }

        if (!this.loader) {
            throw new Error(`Template engine: no loader configured — cannot load "${name}"`)
        }

        const tpl = await this.loader(name)
        this.templateCache.set(name, tpl)
        return tpl
    }

    /**
     * Parses a raw template string into an AST, returning from cache on hit.
     *
     * @param {string} templateStr - Raw template string.
     * @returns {Array<Object>} AST nodes.
     */
    parseTemplate(templateStr) {
        if (this.astCache.has(templateStr)) {
            return this.astCache.get(templateStr)
        }

        const ast = parse(templateStr)
        this.astCache.set(templateStr, ast)
        return ast
    }

    /**
     * Loads a template by name and parses it into an AST (both cached).
     *
     * @param {string} name - Template slug / name.
     * @returns {Promise<Array<Object>>} AST nodes.
     */
    async loadAndParse(name) {
        const tpl = await this.loadTemplate(name)
        return this.parseTemplate(tpl)
    }

    // ── Rendering ─────────────────────────────────────────────────────────

    /**
     * Loads a template by name from the server (or cache) and renders it
     * with the supplied data.  Partials ({{> name}}) inside the template
     * are resolved recursively.
     *
     * @param {string} name - Template slug (e.g. "my-visits-main").
     * @param {Object} data - Data context for rendering.
     * @returns {Promise<string>} Rendered HTML string.
     */
    async render(name, data = {}) {
        const ast = await this.loadAndParse(name)
        const context = new TemplateContext(data)
        return renderNodes(ast, context, this)
    }

    /**
     * Renders an inline template string with the supplied data.
     * Async because included partials ({{> x}}) may trigger loads.
     *
     * @param {string} template - Raw template string.
     * @param {Object} data     - Data context for rendering.
     * @returns {Promise<string>} Rendered HTML string.
     */
    async renderString(template, data = {}) {
        const ast = this.parseTemplate(template)
        const context = new TemplateContext(data)
        return renderNodes(ast, context, this)
    }

    // ── Tree rendering (bottom-up composition) ──────────────────────────

    /**
     * Parses a node descriptor to extract template name and content key.
     * Supports "template:key" syntax where key is the variable name used
     * to inject child content. Defaults to "content" if no key specified.
     *
     * @param {string} descriptor - Template name, optionally with ":keyName".
     * @returns {{ name: string, key: string }} Parsed template name and injection key.
     *
     * @example
     * parseNodeDescriptor("my-visits-main:visitCards")
     * // → { name: "my-visits-main", key: "visitCards" }
     *
     * parseNodeDescriptor("visit-list-wrapper")
     * // → { name: "visit-list-wrapper", key: "content" }
     */
    parseNodeDescriptor(descriptor) {
        const colonIndex = descriptor.indexOf(":")
        if (colonIndex === -1) {
            return { name: descriptor, key: "content" }
        }
        return {
            name: descriptor.substring(0, colonIndex),
            key: descriptor.substring(colonIndex + 1),
        }
    }

    /**
     * Renders a nested template tree from innermost to outermost (bottom-up).
     * Each level renders its children first, then injects the output into itself.
     *
     * ── Tree format ──────────────────────────────────────────────────────────
     *
     * Each node is either:
     *   - "templateName"              → leaf, rendered directly
     *   - "templateName:keyName"      → specifies which variable receives child output
     *   - ["templateName:key", child] → parent with nested child
     *   - "a[b[c[d]]]"               → bracket-notation string (parsed into nested array)
     *
     * ── Looping ──────────────────────────────────────────────────────────────
     *
     * The innermost (leaf) template loops over `data.items` if it's an array.
     * Each item object is spread into the data context for that iteration.
     *
     * ── Examples ─────────────────────────────────────────────────────────────
     *
     * 2-level (cards into main):
     *   renderTree(["my-visits-main:visitCards", ["visitcard"]], data)
     *
     * 3-level (cards → wrapper → main):
     *   renderTree(["my-visits-main:visitCards", ["visit-list-wrapper", ["visitcard"]]], data)
     *
     * 4-level (button → footer → card → wrapper → main):
     *   renderTree(["main:visitCards", ["wrapper", ["card", ["footer"]]]], data)
     *
     * @param {string|Array} tree - Template node or nested array.
     * @param {Object} data       - Data context for rendering.
     * @param {Array}  [data.items] - If present at leaf level, loops the leaf per item.
     * @param {string} [data.emptyTemplate] - Template to render when items is empty.
     * @returns {Promise<string>} Rendered HTML string.
     */
    /**
     * Parses a bracket-notation string into the nested array tree format.
     * Supports the shorthand: "a[b[c[d]]]" → ["a", ["b", ["c", ["d"]]]]
     *
     * @param {string} treeString - Bracket-notation tree string.
     * @returns {Array} Nested array tree.
     *
     * @example
     * parseTreeString("my-visits-page[my-visits-container[my-visits-calendar[my-visits-card]]]")
     * // → ["my-visits-page", ["my-visits-container", ["my-visits-calendar", ["my-visits-card"]]]]
     */
    parseTreeString(treeString) {
        // Trim leading/trailing whitespace to support multi-line bracket strings
        const trimmed = treeString.trim()
        const bracketIndex = trimmed.indexOf("[")

        // No brackets → leaf node, return trimmed name
        if (bracketIndex === -1) {
            return trimmed
        }

        const name = trimmed.substring(0, bracketIndex).trim()
        // Extract inner content (strip outer brackets)
        const inner = trimmed.substring(bracketIndex + 1, trimmed.lastIndexOf("]"))

        // Recursively parse the inner content
        const child = this.parseTreeString(inner)

        // Wrap child in array if it's a string (leaf), otherwise it's already an array
        return [name, Array.isArray(child) ? child : [child]]
    }

    /**
     * Renders a nested template tree bottom-up, with optional React island support.
     *
     * ── Data format ──────────────────────────────────────────────────────────
     *
     * New format (preferred when mixing templates + React islands):
     *   {
     *     templates:  { ...templateData },    // fed to every template render
     *     components: { componentName: { ...props } }  // props per island
     *   }
     *
     * Legacy format (flat object — fully backward-compatible):
     *   { items: [...], someKey: "value", ... }
     *
     * @param {string|Array} tree - Template node or nested array / bracket string.
     * @param {Object} data       - Rendering context (new or legacy format).
     * @returns {Promise<string>} Rendered HTML string.
     *   React island nodes are represented as <div data-react-id="…"> placeholders.
     *   Call tplEngine.hydrateInto(container) after setting innerHTML.
     */
    async renderTree(tree, data = {}) {
        // ── Normalise data format on the public entry-point ──────────────────
        let templateData, componentPropsMap

        if (
            data &&
            typeof data.templates === "object" &&
            data.templates !== null &&
            !Array.isArray(data.templates)
        ) {
            // New format: { templates: {...}, components: {...} }
            templateData = data.templates
            componentPropsMap =
                data.components && typeof data.components === "object" && !Array.isArray(data.components)
                    ? data.components
                    : {}
        } else {
            // Legacy format: data is directly the template context
            templateData = data
            componentPropsMap = {}
        }

        // Reset mount queue for this render pass
        this._mountQueue = []

        return this._renderTreeNode(tree, templateData, componentPropsMap)
    }

    /**
     * Internal recursive tree renderer.
     * Operates on already-normalised templateData and componentPropsMap.
     *
     * @param {string|Array} tree            - Tree node.
     * @param {Object}       templateData    - Flat template data context.
     * @param {Object}       componentPropsMap - Map of component name → props.
     * @returns {Promise<string>} Rendered HTML (may include placeholder divs).
     */
    async _renderTreeNode(tree, templateData, componentPropsMap) {
        // Support bracket-notation string: "a[b[c[d]]]" (including multi-line)
        if (typeof tree === "string" && tree.includes("[")) {
            tree = this.parseTreeString(tree)
        }

        // ── Leaf node (plain string) ─────────────────────────────────────────
        if (typeof tree === "string") {
            const { name } = this.parseNodeDescriptor(tree.trim())

            // React island leaf (no children)
            if (this.componentRegistry.has(name)) {
                return this._emitPlaceholder(name, componentPropsMap[name] || {}, "")
            }

            // Standard template leaf — loop if items array present
            if (Array.isArray(templateData.items)) {
                if (templateData.items.length === 0 && templateData.emptyTemplate) {
                    return this.render(templateData.emptyTemplate, templateData)
                }
                const results = await Promise.all(
                    templateData.items.map((item) => this.render(name, { ...templateData, ...item }))
                )
                return results.join("")
            }
            return this.render(name, templateData)
        }

        if (!Array.isArray(tree) || tree.length === 0) {
            throw new Error("renderTree: tree must be a string or a non-empty array")
        }

        const [descriptor, ...children] = tree
        const { name, key } = this.parseNodeDescriptor(descriptor.trim())

        // ── Node with no children → treat as leaf ────────────────────────────
        if (children.length === 0) {
            if (this.componentRegistry.has(name)) {
                return this._emitPlaceholder(name, componentPropsMap[name] || {}, "")
            }
            if (Array.isArray(templateData.items)) {
                if (templateData.items.length === 0 && templateData.emptyTemplate) {
                    return this.render(templateData.emptyTemplate, templateData)
                }
                const results = await Promise.all(
                    templateData.items.map((item) => this.render(name, { ...templateData, ...item }))
                )
                return results.join("")
            }
            return this.render(name, templateData)
        }

        // ── Render all children first (bottom-up) ────────────────────────────
        let childContent = ""
        for (const child of children) {
            childContent += await this._renderTreeNode(child, templateData, componentPropsMap)
        }

        // ── React island node: emit placeholder, enqueue for hydration ────────
        if (this.componentRegistry.has(name)) {
            return this._emitPlaceholder(name, componentPropsMap[name] || {}, childContent)
        }

        // ── Standard template node: inject child output under the node's key ──
        const { items, emptyTemplate, ...parentData } = templateData
        return this.render(name, { ...parentData, [key]: childContent })
    }

    /**
     * Generates a React island placeholder <div> and pushes an entry onto
     * the internal mount queue for later hydration.
     *
     * @param {string} name        - Registered component name.
     * @param {Object} props       - Props to forward to the React component.
     * @param {string} childrenHtml - Rendered HTML of nested template children.
     * @returns {string} HTML placeholder string.
     */
    _emitPlaceholder(name, props, childrenHtml) {
        const id = generatePlaceholderId()
        this._mountQueue.push({
            id,
            component: this.componentRegistry.get(name),
            props,
            childrenHtml,
        })
        return `<div data-react-component="${name}" data-react-id="${id}"></div>`
    }

    // ── React island support ───────────────────────────────────────────────

    /**
     * Registers a React component so it can be used as a node in the template tree.
     *
     * @param {string} name - Node name to use in the tree string / array.
     * @param {React.ComponentType} component - React component constructor or function.
     *
     * @example
     * tplEngine.registerComponent("mapComponent", MyVisitsMap)
     *
     * // Then in the tree:
     * // "page[container[mapComponent[my-visits-card]]]"
     */
    registerComponent(name, component) {
        this.componentRegistry.register(name, component)
    }

    /**
     * Returns the mount queue populated by the most recent renderTree() call.
     * Entries describe every React island that needs to be hydrated.
     *
     * @returns {Array<{id: string, component: React.ComponentType, props: Object, childrenHtml: string}>}
     */
    getMountQueue() {
        return this._mountQueue
    }

    /**
     * Mounts all pending React island components into their placeholder elements.
     *
     * Call this AFTER setting container.innerHTML = html:
     *
     *   const html = await tplEngine.renderTree(tree, data)
     *   shellRef.current.innerHTML = html
     *   tplEngine.hydrateInto(shellRef.current)
     *
     * Automatically unmounts any previously mounted React roots to prevent leaks.
     *
     * @param {HTMLElement} container - The DOM element whose innerHTML was set.
     */
    hydrateInto(container) {
        this.unmountAll()
        this._reactRoots = hydrateComponents(container, this._mountQueue)
    }

    /**
     * Unmounts all React roots created by the most recent hydrateInto() call.
     * Called automatically by hydrateInto() — invoke manually only when you
     * are removing the container from the DOM entirely.
     */
    unmountAll() {
        unmountComponents(this._reactRoots)
        this._reactRoots = []
    }

    // ── Partials registration ─────────────────────────────────────────────

    /**
     * Registers an inline partial so {{> name}} can resolve it without a
     * network round-trip.
     *
     * @param {string} name     - Partial name used in {{> name}}.
     * @param {string} template - Raw template string for this partial.
     */
    registerPartial(name, template) {
        this.templateCache.set(name, template)
        // Invalidate AST cache for this template in case it was previously cached
        this.astCache.delete(template)
    }

    // ── Preloading ────────────────────────────────────────────────────────

    /**
     * Pre-fetches and caches multiple templates in parallel.
     * Useful for loading a template tree upfront.
     *
     * @param {string[]} names - Array of template slugs to preload.
     * @returns {Promise<void>}
     */
    async preload(names) {
        await Promise.all(names.map((name) => this.loadTemplate(name)))
    }

    // ── Cache management ──────────────────────────────────────────────────

    /**
     * Clears all template and AST caches.
     */
    clearCache() {
        this.templateCache.clear()
        this.astCache.clear()
    }
}

/**
 * Factory function — creates a TemplateEngine with the given loader.
 *
 * @param {Object}   options        - Configuration.
 * @param {Function} options.loader - Async function (name) → templateString.
 * @returns {TemplateEngine} A new engine instance.
 *
 * @example
 * const engine = createTemplateEngine({ loader: loadtplTemplate })
 * const html   = await engine.render("my-visits-main", { title: "Schedule" })
 */
export function createTemplateEngine(options = {}) {
    return new TemplateEngine(options)
}
